import { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import { getCaseDetails } from 'services/CaseServices';
import { listTimeEntries, createTimeEntry, updateTimeEntry, deleteTimeEntry } from 'services/timerServices';
import { extractCaseId, getTenantDetails } from 'utils/utils';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useDataProvider } from 'context/DataProviderContext';
import { useUserDetailsContext } from 'context/userDetailsContext';

const getIndexofArrayByKeyValue = (array = [], key = false, value = false) => {
  let index = false;
  if (array && array.length && key && value) {
    array.find((item, i) => {
      let result = false;
      if (item[key] === value) {
        index = i;
        return true;
      }
      return result;
    });
  }
  return index;
};

export default function useTimerManager() {
  const { timerList, setTimerList } = useDataProvider();
  const [caseSearchValue, setCaseSearchValue] = useState('');
  const [showTimerManagement, setShowTimerManagement] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [timersReadyToSave, setTimersReadyToSave] = useState(new Set());
  const timerCaseOverlay = useRef(null);

  const { pathname } = useLocation();
  const { addToast } = useToast();
  const [tenantId, tenantSk] = getTenantDetails();
  const { lastFetchedTenantKey, setLastFetchedTenantKey } = useUserDetailsContext();

  const debouncedUpdateTimeEntry = useCallback(
    debounce((data) => {
      updateTimeEntry(data, false, '', true, true)
        .then(() => {})
        .catch(() => {});
    }, 500),
    []
  );

  const onChangeNotes = (data, id) => {
    let index = getIndexofArrayByKeyValue(timerList, 'sk', id);
    if (index === -1) return;

    let timerList_ = [...timerList];
    timerList_[index] = { ...timerList_[index], notes: data };

    setTimerList(timerList_);
    if (timerList_[index]?.time_entry_status !== 'READY_TO_SAVE') {
      debouncedUpdateTimeEntry(timerList_[index]);
    }
  };

  const onChangeBillingCode = (billingCode, id) => {
    let index = getIndexofArrayByKeyValue(timerList, 'sk', id);
    let timerList_ = [...timerList];
    timerList_[index] = {
      ...timerList_[index],
      activity_type: billingCode?.activity_type,
      activity_type_code: billingCode?.activity_type_code,
      activity_type_id: billingCode?.activity_type_id,
      activity_type_sk: billingCode?.sk,
    };
    setTimerList(timerList_);
    if (timerList_[index]?.time_entry_status !== 'READY_TO_SAVE') {
      debouncedUpdateTimeEntry(timerList_[index]);
    }
  };

  const loadTimer = () => {
    listTimeEntries(
      {
        case_id: null,
        time_entry_date: null,
        time_entry_status: 'PROGRESS',
        time_entry_from: '',
        time_entry_to: '',
        time_entry_filter: 0,
        search_key: '',
      },
      false,
      undefined,
      undefined,
      undefined,
      undefined,
      true
    )
      .then((response) => {
        if (response?.data?.time_entries) {
          let running_timer = response.data.time_entries.filter((index) => !index.end_time);
          let remaining_timer = response.data.time_entries.filter((index) => index.end_time);
          setTimerList([...running_timer, ...remaining_timer]);
        }
      })
      .catch(() => {});
  };

  const createTimerSuccess = () => {
    loadTimer();
  };

  const handleTimerButtonClick = (e) => {
    const caseId = extractCaseId(pathname);
    const activeTimers = timerList?.filter((timer) => timer.time_entry_status !== 'COMPLETED') || [];

    if (caseId) {
      if (activeTimers.length === 0) {
        createTimerForCase(caseId, e);
      } else {
        timerCaseOverlay.current?.toggle(e);
        setShowTimerManagement(true);
      }
    } else {
      timerCaseOverlay.current?.toggle(e);
      if (activeTimers.length > 0) {
        setShowTimerManagement(true);
      } else {
        setShowTimerManagement(false);
      }
    }
  };

  const handleStartNewTimer = async () => {
    const caseId = extractCaseId(pathname);
    if (caseId) {
      // Proactively check for an existing timer for this case and resume/show instead of creating a new one
      let caseName = '';
      let uniqueNumber = '';
      try {
        const [tId, tSk] = getTenantDetails();
        const resp = await getCaseDetails(tId, tSk, '', '', {}, caseId);
        caseName = resp?.data?.case_description?.case_name || '';
        uniqueNumber = resp?.data?.unique_number || '';
      } catch (_) {}

      const normalized = (s) => (s || '').toString().trim().toLowerCase();
      const existingTimer = (timerList || []).find(
        (t) =>
          t.time_entry_status !== 'COMPLETED' &&
          (t.case_id === caseId ||
            (uniqueNumber && t.unique_number === uniqueNumber) ||
            (!t.case_id && caseName && normalized(t.case_name) === normalized(caseName)))
      );

      if (existingTimer) {
        if (existingTimer.start_time && !existingTimer.end_time) {
          setShowTimerManagement(true);
          return;
        }
        handleTimerResume(existingTimer);
        setShowTimerManagement(true);
        return;
      }

      createTimerForCase(caseId, null);
    } else {
      setShowTimerManagement(false);
    }
  };

  const createTimerForCase = async (caseId, e) => {
    // Try to resolve case name for fallback matching when timers may not have case_id
    let caseName = '';
    let uniqueNumber = '';
    try {
      const [tId, tSk] = getTenantDetails();
      const resp = await getCaseDetails(tId, tSk, '', '', {}, caseId);
      caseName = resp?.data?.case_description?.case_name || '';
      uniqueNumber = resp?.data?.unique_number || '';
    } catch (_) {}

    const normalized = (s) => (s || '').toString().trim().toLowerCase();
    const existingTimer = (timerList || []).find(
      (t) =>
        t.time_entry_status !== 'COMPLETED' &&
        (t.case_id === caseId ||
          (uniqueNumber && t.unique_number === uniqueNumber) ||
          (!t.case_id && caseName && normalized(t.case_name) === normalized(caseName)))
    );
    if (existingTimer) {
      // If already running, just open management without resetting the clock
      if (existingTimer.start_time && !existingTimer.end_time) {
        if (e) timerCaseOverlay.current?.toggle(e);
        setShowTimerManagement(true);
        return;
      }
      // Resume if paused
      handleTimerResume(existingTimer);
      if (e) timerCaseOverlay.current?.toggle(e);
      setShowTimerManagement(true);
      return;
    }

    const currentlyRunning = timerList.filter((t) => t.start_time && !t.end_time && t.time_entry_status !== 'COMPLETED');

    if (currentlyRunning.length > 0) {
      const pausePromises = currentlyRunning.map((runningTimer) => {
        const now = new Date().getTime();
        let updatedTimer = { ...runningTimer };
        updatedTimer.end_time = now;

        let previousMs = 0;
        if (runningTimer.total_times && runningTimer.total_times !== '00:00:00') {
          const [h, m, s] = runningTimer.total_times.split(':').map(Number);
          previousMs = ((h || 0) * 3600 + (m || 0) * 60 + (s || 0)) * 1000;
        } else if (runningTimer.total_hours && parseFloat(runningTimer.total_hours) > 0) {
          previousMs = parseFloat(runningTimer.total_hours) * 3600 * 1000;
        }

        const elapsed = runningTimer.start_time ? now - runningTimer.start_time : 0;
        const totalMs = previousMs + elapsed;

        const totalSeconds = Math.floor(totalMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        updatedTimer.total_times = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
          .toString()
          .padStart(2, '0')}`;
        updatedTimer.total_hours = (totalMs / (3600 * 1000)).toFixed(2);
        updatedTimer.start_time = '';

        return updateTimeEntry(updatedTimer);
      });

      Promise.all(pausePromises)
        .then(() => {
          createNewTimer(caseId, e);
        })
        .catch(() => {
          createNewTimer(caseId, e);
        });
    } else {
      createNewTimer(caseId, e);
    }
  };

  const createNewTimer = async (caseId, e) => {
    let data = {
      start_time: new Date().getTime(),
      time_entry_status: 'PROGRESS',
      billable: true,
      hour_rate: '',
      total_times: '00:00:00',
      total_hours: '0',
      case_id: caseId,
    };

    try {
      const [tId, tSk] = getTenantDetails();
      const resp = await getCaseDetails(tId, tSk, '', '', {}, caseId);
      const caseName = resp?.data?.case_description?.case_name || '';
      if (caseName) data.case_name = caseName;
    } catch (_) {}

    let date = new Date();
    let dd = String(date.getDate()).padStart(2, '0');
    let mm = String(date.getMonth() + 1).padStart(2, '0');
    let yyyy = date.getFullYear();
    date = yyyy + '-' + mm + '-' + dd;
    data.time_entry_date = date;

    createTimeEntry(data)
      .then(() => {
        createTimerSuccess();
        if (e) {
          timerCaseOverlay.current?.toggle(e);
        }
        setShowTimerManagement(true);
      })
      .catch(() => {});
  };

  const handleTimerResume = (timer) => {
    const currentlyRunning = timerList.filter(
      (t) => t.start_time && !t.end_time && t.sk !== timer.sk && t.time_entry_status !== 'COMPLETED'
    );

    if (currentlyRunning.length > 0) {
      currentlyRunning.forEach((runningTimer) => {
        const now = new Date().getTime();
        let updatedTimer = { ...runningTimer };
        updatedTimer.end_time = now;

        let previousMs = 0;
        if (runningTimer.total_times && runningTimer.total_times !== '00:00:00') {
          const [h, m, s] = runningTimer.total_times.split(':').map(Number);
          previousMs = ((h || 0) * 3600 + (m || 0) * 60 + (s || 0)) * 1000;
        } else if (runningTimer.total_hours && parseFloat(runningTimer.total_hours) > 0) {
          previousMs = parseFloat(runningTimer.total_hours) * 3600 * 1000;
        }

        const elapsed = runningTimer.start_time ? now - runningTimer.start_time : 0;
        const totalMs = previousMs + elapsed;

        const totalSeconds = Math.floor(totalMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        updatedTimer.total_times = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
          .toString()
          .padStart(2, '0')}`;
        updatedTimer.total_hours = (totalMs / (3600 * 1000)).toFixed(2);
        updatedTimer.start_time = '';

        updateTimeEntry(updatedTimer).catch(() => loadTimer());
      });
    }

    let updatedTimer = { ...timer };
    updatedTimer.start_time = new Date().getTime();
    updatedTimer.end_time = '';

    const updatedTimerList = timerList.map((t) => {
      if (currentlyRunning.some((running) => running.sk === t.sk)) {
        const runningTimer = currentlyRunning.find((running) => running.sk === t.sk);
        const now = new Date().getTime();
        let pausedTimer = { ...runningTimer };
        pausedTimer.end_time = now;
        pausedTimer.start_time = '';

        let previousMs = 0;
        if (runningTimer.total_times && runningTimer.total_times !== '00:00:00') {
          const [h, m, s] = runningTimer.total_times.split(':').map(Number);
          previousMs = ((h || 0) * 3600 + (m || 0) * 60 + (s || 0)) * 1000;
        } else if (runningTimer.total_hours && parseFloat(runningTimer.total_hours) > 0) {
          previousMs = parseFloat(runningTimer.total_hours) * 3600 * 1000;
        }

        const elapsed = runningTimer.start_time ? now - runningTimer.start_time : 0;
        const totalMs = previousMs + elapsed;

        const totalSeconds = Math.floor(totalMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        pausedTimer.total_times = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
          .toString()
          .padStart(2, '0')}`;
        pausedTimer.total_hours = (totalMs / (3600 * 1000)).toFixed(2);

        return pausedTimer;
      }
      return t.sk === timer.sk ? updatedTimer : t;
    });
    setTimerList(updatedTimerList);

    updateTimeEntry(updatedTimer).catch(() => loadTimer());
  };

  const getRunningTimersCount = () => {
    return timerList?.filter((timer) => !timer.end_time && timer.time_entry_status !== 'COMPLETED')?.length || 0;
  };

  const getPausedTimersCount = () => {
    return timerList?.filter((timer) => timer.start_time === '' && timer.end_time && timer.time_entry_status !== 'COMPLETED')?.length || 0;
  };

  const getTotalActiveTimersCount = () => {
    return timerList?.filter((timer) => timer.time_entry_status !== 'COMPLETED')?.length || 0;
  };

  const handleDeleteTimer = (timer) => {
    deleteTimeEntry(timer.sk)
      .then(() => {
        loadTimer();
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Timer deleted successfully');
      })
      .catch(() => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'Failed to delete timer');
      });
  };

  const handleSaveTimer = (timer) => {
    let timerToSave = { ...timer };
    timerToSave.time_entry_status = 'COMPLETED';

    updateTimeEntry(timerToSave)
      .then(() => {
        setTimersReadyToSave((prev) => {
          const newSet = new Set(prev);
          newSet.delete(timer.sk);
          return newSet;
        });
        loadTimer();
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Timer saved successfully');
      })
      .catch(() => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, 'Failed to save timer');
      });
  };

  const handleKeyboardShortcuts = useCallback(
    (event) => {
      const isInputFocused =
        document.activeElement &&
        (document.activeElement.tagName === 'INPUT' ||
          document.activeElement.tagName === 'TEXTAREA' ||
          document.activeElement.contentEditable === 'true');

      if (event.key === 'Escape') {
        if (timerCaseOverlay.current) {
          timerCaseOverlay.current.hide();
        }
        return;
      }

      if (isInputFocused) return;

      if (event.ctrlKey && event.key === 'n') {
        event.preventDefault();
        handleTimerButtonClick(event);
        return;
      }

      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        const readyToSaveTimer = timerList.find((t) => t.time_entry_status === 'READY_TO_SAVE');
        if (readyToSaveTimer) {
          handleSaveTimer(readyToSaveTimer);
        }
        return;
      }

      if (event.key === 'Enter' && !event.ctrlKey) {
        const readyToSaveTimer = timerList.find((t) => t.time_entry_status === 'READY_TO_SAVE');
        if (readyToSaveTimer) {
          const descriptionField = document.querySelector('textarea[placeholder*="description"], input[placeholder*="description"]');
          if (descriptionField) {
            descriptionField.focus();
          }
        }
        return;
      }
    },
    [timerList]
  );

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, '0')}\u2009:\u2009${minutes.toString().padStart(2, '0')}\u2009:\u2009${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  const getTimerDisplay = (timer) => {
    if (timer.start_time && !timer.end_time) {
      const elapsed = currentTime - timer.start_time;

      let previousTime = 0;
      if (timer.total_times) {
        const [h, m, s] = timer.total_times.split(':').map(Number);
        previousTime = ((h || 0) * 3600 + (m || 0) * 60 + (s || 0)) * 1000;
      } else if (timer.total_hours) {
        previousTime = parseFloat(timer.total_hours) * 3600 * 1000;
      }

      return formatTime(elapsed + previousTime);
    } else {
      return timer.total_times || '00:00:00';
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardShortcuts);
    return () => {
      document.removeEventListener('keydown', handleKeyboardShortcuts);
    };
  }, [handleKeyboardShortcuts]);

  useEffect(() => {
    if (tenantId && tenantSk) {
      const currentKey = `${tenantId}:${tenantSk}`;
      if (lastFetchedTenantKey !== currentKey) {
        loadTimer();
        setLastFetchedTenantKey(currentKey);
      }
    }
  }, [tenantId, tenantSk]); // eslint-disable-line

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const readyToSaveTimer = timerList.find((t) => t.time_entry_status === 'READY_TO_SAVE');
    if (readyToSaveTimer) {
      setTimeout(() => {
        const descriptionField = document.querySelector('textarea[placeholder*="description"], textarea');
        if (descriptionField) {
          descriptionField.focus();
          const length = descriptionField.value.length;
          descriptionField.setSelectionRange(length, length);
        }
      }, 300);
    }
  }, [timersReadyToSave, timerList]);

  return {
    timerCaseOverlay,
    timerList,
    setTimerList,
    caseSearchValue,
    setCaseSearchValue,
    showTimerManagement,
    setShowTimerManagement,
    onChangeNotes,
    onChangeBillingCode,
    loadTimer,
    getTimerDisplay,
    getRunningTimersCount,
    getPausedTimersCount,
    getTotalActiveTimersCount,
    handleDeleteTimer,
    handleStartNewTimer,
    handleTimerButtonClick,
  };
}
